import { useAppSelector } from './reduxTKHooks';
import { RootState } from '../stores/store';
import { en, TKeys } from '../i18n/en';
import { fr } from '../i18n/fr';
import React from 'react';

export type TParams = Record<string, string | number>;

/* A date, a price or a day count cannot be concatenated around a translated
   fragment: word order differs per language. Placeholders let each locale put
   the value where it belongs. An unknown placeholder is left as written, so a
   typo shows up in the UI instead of silently vanishing. */
const interpolate = (template: string, params: TParams) =>
  template.replace(/{(\w+)}/g, (match, name) => (name in params ? String(params[name]) : match));

export enum AppLangs {
  fr = 'fr',
  en = 'en',
}

export const useTranslation = () => {
  const language = useAppSelector((state: RootState) => state.app.currentAppLang);

  // to memoize if language did not change, as every screens will have them
  return React.useCallback(
    (key?: TKeys, params?: TParams) => {
      if (!key) return '';
      const fallback = en[key] || key;
      const template = (() => {
        switch (language) {
          case AppLangs.fr:
            return fr[key] || fallback;
          case AppLangs.en:
            return en[key] || fallback;
          default:
            return fallback;
        }
      })();
      return params ? interpolate(template, params) : template;
    },
    [language]
  );
};

/*
   'fr-FR':
   'fr-BE':
   'en-US':
   'en-UK':
 */

export const getAppLang = (sysLang: string = 'en') =>
  Object.values(AppLangs).find((ln) => sysLang.toLowerCase().startsWith(ln)) || AppLangs.en;
